import { Injectable } from '@angular/core';
import { SHUFFLE_MAPPER } from '../constants/shuffle-mapper';
import { bufferToHex, fromHexString, shuffleArray } from '@client-monorepo/common/utilities';

@Injectable({
  providedIn: 'root',
})
export class C2cEcdhService {
  /**
   * used to x509 format encoding
   * is standard defining the format of public key
   */
  private PUBLIC_KEY_HEADER = atob('MzA1OTMwMTMwNjA3MmE4NjQ4Y2UzZDAyMDEwNjA4MmE4NjQ4Y2UzZDAzMDEwNzAzNDIwMA==');

  /**
   * generated once and used everywhere
   */
  clientPairKey!: {
    publicKey: CryptoKey;
    privateKey: CryptoKey;
  };

  serverTraceData: {
    publicKey: string;
    randomNumber: string;
  } = {
    publicKey: '',
    randomNumber: '',
  };

  private LENGTHS: number[] = [25, 35, 45, 50, 30];

  private MAX_RANDOM = 999;

  constructor() {
    // todo new setLength and MAX_RANDOM
  }

  private setLength() {
    const base = atob('TUQtNS0o');
    const str = atob('UlNBLQ==');
    const output = [];
    for (let i = 0; i < str.length; i++) {
      output.push(base.charCodeAt(5) + i * (base.charCodeAt(5) / 10));
    }
    this.LENGTHS = output;
  }

  /**
   * called once to generate clientPairKey
   */
  private async generatePairKey() {
    this.clientPairKey = await window.crypto.subtle.generateKey(
      {
        name: atob('RUNESA=='),
        namedCurve: atob('UC0yNTY='),
      },
      true, // no need to make client's private key exportable
      ['deriveKey', 'deriveBits'],
    );
    return this.clientPairKey;
  }

  /**
   * generate client public key clientPairKey and
   * used in the generateClientTraceCode function
   * clientPublicKeyX509 returns an upperCase string with a length of 182
   * upperCase is contract
   */
  private async generatePublicKey(): Promise<string> {
    const pairKey = await this.generatePairKey();
    const clientPublicKey = await window.crypto.subtle.exportKey('raw', pairKey.publicKey);
    const clientPublicKeyHex = bufferToHex(clientPublicKey);
    const clientPublicKeyX509 = this.PUBLIC_KEY_HEADER + clientPublicKeyHex;
    return clientPublicKeyX509.toUpperCase();
  }

  /**
   * generate sharedSecret of clientPrivateKey and
   * serverPublicKey
   */
  private async generateSharedSecret(clientPrivateKey: any, pureServerPublicKey: any): Promise<string> {
    let serverPublicKey = pureServerPublicKey;
    if (serverPublicKey.length === 182) {
      const decodeX509 = serverPublicKey.substring(this.PUBLIC_KEY_HEADER.length);
      serverPublicKey = fromHexString(decodeX509);
    } else if (serverPublicKey.length === 130) {
      serverPublicKey = fromHexString(pureServerPublicKey);
    }
    const serverPublicKeyImported = await window.crypto.subtle.importKey(
      'raw',
      serverPublicKey, // already converted to ArrayBuffer format
      {
        name: atob('RUNESA=='),
        namedCurve: atob('UC0yNTY='),
      },
      true,
      [],
    );

    const sharedSecret = await window.crypto.subtle.deriveBits(
      {
        name: atob('RUNESA=='),
        namedCurve: atob('UC0yNTY='),
        public: serverPublicKeyImported,
      } as EcdhKeyDeriveParams,
      clientPrivateKey,
      256,
    );
    //  uppercase is contract
    return bufferToHex(sharedSecret).toUpperCase();
  }

  /**
   * generate traceCode based on contract contains:
   * 1) A 5-digit number including the numbers 0 to 4
   * 2) A multi-digit random number between 0 and n = 999
   * 3) A 182-char public key with EC algorithm
   * contractTraceCode returns a string with length of: 182-char + 5-digit number mapped + multi-digit random number length
   */
  async generateClientTraceCode(): Promise<string> {
    const shuffledOrderArray = this.generateShuffleKeyOrder(5);
    const randomMultiDigitString = this.generateMultiDigitRandomNumber(this.MAX_RANDOM);
    const minusRandomMultiDigitString = this.getMinusMultiDigitRandomNumberAsString(this.MAX_RANDOM, randomMultiDigitString);
    const clientPublicKey = await this.generatePublicKey();
    const contractKey = minusRandomMultiDigitString + clientPublicKey;
    const shuffledContractCode = this.generateShuffledContractCode(shuffledOrderArray, contractKey);
    const mappedShuffleArray = this.shuffleOrderMapper(shuffledOrderArray);
    const [firstSlice, secondSlice] = this.generateSlicesOfMappedShuffleArray(mappedShuffleArray, 2, 3);
    return firstSlice + shuffledContractCode + secondSlice;
  }

  /**
   * generate random array with partsNumber length
   * used to shuffle clientPublicKey based on items
   */
  private generateShuffleKeyOrder(partsNumber: number): number[] {
    return shuffleArray(Array.from(Array(partsNumber).keys()));
  }

  /**
   * generate random number between 0 and n=999
   * return string because we need exact n digit
   * to concat "random number" & "clientPublicKey hex format"
   */
  private generateMultiDigitRandomNumber(digitNumber: number): string {
    const randomDigit = Math.floor(Math.random() * (digitNumber + 1));
    const randomDigitString = randomDigit.toString();
    const digitNumberString = digitNumber.toString();
    return randomDigitString.padStart(digitNumberString.length, '0');
  }

  private getMinusMultiDigitRandomNumberAsString(digitNumber: number, randomNumber: string): string {
    const minusNumberAsString = `${Number(digitNumber) - Number(randomNumber)}`;
    return minusNumberAsString.padStart(digitNumber.toString().length, '0');
  }

  private generateShuffledContractCode(orderArray: number[], customValue: string): string {
    const bucksCustomValue = this.generateBucksOfString(customValue, this.LENGTHS);
    const sortedArray: Array<string> = [];
    orderArray.map((item, index) => (sortedArray[item] = bucksCustomValue[index]));
    return sortedArray.join('');
  }

  /**
   * generate equal parts of string to the desired length
   * return string because we need exact two digit
   * to concat number & clientPublicKey hex format
   */
  private generateBucksOfString(customValue: string, lengths: number[]): string[] {
    let startIndex = 0;
    return lengths.reduce((resultArray: Array<string>, length) => {
      const item = customValue.substr(startIndex, length);
      startIndex += length;
      return resultArray.concat(item);
    }, []);
  }

  private shuffleOrderMapper(pureArray: number[]): Array<number | string> {
    const mappedOrder: Array<number | string> = [];
    pureArray.map((item, index) => (mappedOrder[index] = SHUFFLE_MAPPER[item]));
    return mappedOrder;
  }

  /**
   * generate slices of mappedArray's items based of length of each slice
   * return an array of strings that each item has length of lengthOfSlices in order
   */
  private generateSlicesOfMappedShuffleArray(mappedArray: Array<number | string>, ...lengthOfSlices: number[]): Array<string> {
    let startIndex = 0;
    const slices = [];
    for (const length of lengthOfSlices) {
      slices.push(mappedArray.slice(startIndex, startIndex + length).join(''));
      startIndex += length;
    }
    return slices;
  }

  private RevertMappedShuffleOrder(mappedArray: Array<number | string>): number[] {
    const reverseMapping: Record<string, number> = {};
    Object.keys(SHUFFLE_MAPPER).forEach((key) => {
      const numericKey = parseInt(key, 10);
      reverseMapping[SHUFFLE_MAPPER[numericKey]] = parseInt(key, 10);
    });
    return mappedArray.map((value) => reverseMapping[value]);
  }

  private RevertShuffledKeyArray(shuffledKey: string, orderArray: number[]): string[] {
    const revertedArray: Array<string> = [];
    let startIndex = 0;
    orderArray.map((order, index) => {
      const foundIndex = orderArray.findIndex((item) => item === index);
      const endIndex = startIndex + this.LENGTHS[foundIndex];
      const slicedString = shuffledKey.slice(startIndex, endIndex);
      startIndex += this.LENGTHS[foundIndex];
      revertedArray[foundIndex] = slicedString;
    });
    return revertedArray;
  }

  extractShuffledKeyAndOrderMapped(traceCode: string) {
    // Extract slice1 from the start
    const slice1 = traceCode.slice(0, 2);

    // Extract slice2 from the end
    const slice2 = traceCode.slice(-3);

    // Extract randomString from the middle
    const shuffledKey = traceCode.slice(2, -3);

    return { shuffledKey, shuffleOrderMapped: [...slice1, ...slice2] };
  }

  /**
   * reverse generateClientTraceCode steps to
   * extract serverPublicKey and serverRandomNumber(n-digit)
   */
  DecodeServerTraceCode(traceCode: string) {
    const { shuffledKey, shuffleOrderMapped } = this.extractShuffledKeyAndOrderMapped(traceCode);
    const revertedShuffleOrderMapped = this.RevertMappedShuffleOrder(shuffleOrderMapped);
    const sortedArray = this.RevertShuffledKeyArray(shuffledKey, revertedShuffleOrderMapped);
    const contractCode = sortedArray.join('');
    const randomNumber = contractCode.substring(0, 3);
    const minusRandomNumber = this.getMinusMultiDigitRandomNumberAsString(this.MAX_RANDOM, randomNumber);
    this.serverTraceData = {
      ...this.serverTraceData,
      publicKey: contractCode.substring(3),
      randomNumber: minusRandomNumber,
    };
    return this.serverTraceData;
  }

  /**
   * fingerprint is a hash based on sharedSecret and contracted string
   */
  async generateFingerPrint(publicKey: string, hmacInput: string, expireDate: string, encryptedPinDto: string): Promise<string> {
    if (!this.clientPairKey) {
      this.clientPairKey = await this.generatePairKey();
    }
    const sharedSecret = await this.generateSharedSecret(this.clientPairKey.privateKey, publicKey);
    const hash = await this.generateHMAC(hmacInput, sharedSecret);
    return this.shuffleHashCode(expireDate, hash, encryptedPinDto);
  }

  private findPartNumber(expireDate: string): number {
    const mod = +(expireDate.split('/').pop() as string) % 2;
    let partNumber: number;
    switch (mod) {
      case 0:
        partNumber = 4;
        break;
      case 1:
        partNumber = 8;
        break;
      default:
        partNumber = 4;
        break;
    }
    return partNumber;
  }

  private shuffleHashCode(expireDate: string, hash: string, encryptedPinDto: string): string {
    const partNumber = this.findPartNumber(expireDate);
    const hashArray = this.divideStatement(hash, partNumber);
    const arrayIndex = this.encodeFingerPrint(encryptedPinDto, partNumber);
    return this.concatStatement(hashArray, arrayIndex);
  }

  private concatStatement(array: any[], arrayIndex: Array<number>): string {
    const outputArray = Array(arrayIndex.length);
    arrayIndex.forEach((target, index) => {
      outputArray[target] = array[index];
    });
    return outputArray.join('');
  }

  private divideStatement(hash: string, partNumber: number) {
    const partLength = Math.ceil(hash.length / partNumber);
    const result = [];

    for (let i = 0; i < partNumber; i++) {
      result.push(hash.substring(i * partLength, (i + 1) * partLength));
    }
    return result;
  }

  getCharactersWithOffset(baseString: string, n: number): Array<string> {
    let extractedString = '';
    const seenCharacters = new Set(); // Set to track characters already added
    let index = n;

    while (extractedString.length < n && index < baseString.length) {
      const char = baseString.charAt(index);

      if (!seenCharacters.has(char)) {
        extractedString += char;
        seenCharacters.add(char);
      }

      index += n;
    }

    return [...extractedString];
  }

  private encodeFingerPrint(fingerPrint: string, partNumber: number) {
    const extractedString = this.getCharactersWithOffset(fingerPrint, partNumber);
    const sortedAsciiCode = extractedString.map((item) => item.charCodeAt(0)).sort((a, b) => a - b);
    return extractedString.map((item) => sortedAsciiCode.indexOf(item.charCodeAt(0)));
  }

  /**
   * generate hash with hmac algorithm
   */
  private async generateHMAC(hmacInput: string, sharedSecret: string) {
    const encoder = new TextEncoder();
    const data = encoder.encode(hmacInput);
    const secretKey = encoder.encode(sharedSecret);
    const sharedSecretImported = await window.crypto.subtle.importKey(
      'raw',
      secretKey,
      { name: atob('SE1BQw=='), hash: atob('U0hBLTI1Ng==') },
      false,
      ['sign', 'verify'],
    );
    const signature = await window.crypto.subtle.sign(atob('SE1BQw=='), sharedSecretImported, data);
    const signatureBuffer = new Uint8Array(signature);
    const signatureHex = bufferToHex(signatureBuffer);
    return signatureHex.toUpperCase();
  }
}
